/* eslint-disable react/prop-types */
import React from 'react';
import styled, { keyframes, useTheme } from 'styled-components';
import Pagination from '../Pagination';
import { tokens, inkA } from '../tokens';
import useIsMobile from '../useIsMobile';

// Card-wrapped table with optional toolbar + filter slots and a pagination
// footer. Desktop = CSS-grid rows; mobile (< 768px) = stacked cards. Pass
// `loading` to render shimmer placeholders.
// columns: [{ key, label, width, align, primary }]. renderRow(item) ->
// { [columnKey]: node|string }. String/number cells get default text styling.

const SKELETON_ROWS = 6;

const pulse = keyframes`
  0% { opacity: 0.55; }
  50% { opacity: 1; }
  100% { opacity: 0.55; }
`;

const SkeletonBar = styled.div`
  flex-shrink: 0;
  height: ${(p) => p.$h || '14px'};
  width: ${(p) => p.$w || '70%'};
  border-radius: ${(p) => p.$r || '7px'};
  background: ${(p) => inkA(p.theme, 0.09)};
  animation: ${pulse} 1.2s ease-in-out infinite;
`;

function HeaderCell({ children, align }) {
  const t = tokens(useTheme());
  return (
    <div
      style={{
        fontSize: 11,
        fontWeight: 800,
        letterSpacing: '0.8px',
        textTransform: 'uppercase',
        color: t.label,
        textAlign: align || 'left',
      }}
    >
      {children}
    </div>
  );
}

function Cell({ content, align }) {
  const theme = useTheme();
  const isText = typeof content === 'string' || typeof content === 'number';
  return (
    <div
      style={{
        minWidth: 0,
        textAlign: align || 'left',
        ...(align === 'right'
          ? { display: 'flex', justifyContent: 'flex-end' }
          : {}),
      }}
    >
      {isText ? (
        <span
          style={{
            display: 'block',
            minWidth: 0,
            fontSize: 14,
            color: inkA(theme, 0.78),
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {content}
        </span>
      ) : (
        content
      )}
    </div>
  );
}

function Row({ cols, columns, cells, last }) {
  const theme = useTheme();
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: cols,
        columnGap: 18,
        alignItems: 'center',
        padding: '15px 24px',
        borderBottom: last ? 'none' : '1px solid ' + inkA(theme, 0.07),
      }}
    >
      {columns.map((c) => (
        <Cell key={c.key} content={cells[c.key]} align={c.align} />
      ))}
    </div>
  );
}

// One column's loading placeholder, shaped by `column.skeleton`:
// 'media' (logo + two lines), 'pill', 'actions' (N squares via actionsCount),
// or a plain text bar (the default for any unconfigured column).
function SkeletonCell({ column }) {
  const shape = column.skeleton || 'text';
  if (shape === 'media') {
    return (
      <>
        <SkeletonBar $w="56px" $h="40px" $r="8px" />
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
            flex: 1,
            minWidth: 0,
          }}
        >
          <SkeletonBar $w="60%" />
          <SkeletonBar $w="40%" $h="11px" />
        </div>
      </>
    );
  }
  if (shape === 'pill') return <SkeletonBar $w="58px" $h="20px" $r="999px" />;
  if (shape === 'actions') {
    return Array.from({ length: column.actionsCount || 3 }).map((_, i) => (
      <SkeletonBar key={i} $w="28px" $h="28px" $r="8px" />
    ));
  }
  return <SkeletonBar $w="65%" />;
}

// Desktop loading row — shimmer placeholders driven by the column config.
function SkeletonRow({ cols, columns, last }) {
  const theme = useTheme();
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: cols,
        columnGap: 18,
        alignItems: 'center',
        padding: '15px 24px',
        borderBottom: last ? 'none' : '1px solid ' + inkA(theme, 0.07),
      }}
    >
      {columns.map((c) => (
        <div
          key={c.key}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: c.skeleton === 'media' ? 15 : 6,
            minWidth: 0,
            justifyContent: c.align === 'right' ? 'flex-end' : 'flex-start',
          }}
        >
          <SkeletonCell column={c} />
        </div>
      ))}
    </div>
  );
}

// Mobile: one stacked card per row — primary cell on top, the rest as
// labelled meta, actions at the bottom.
function MobileCard({ columns, cells, last }) {
  const theme = useTheme();
  const t = tokens(theme);
  const primary = columns.find((c) => c.primary) || columns[0];
  const actionsCol = columns.find((c) => c.key === 'actions');
  const metaCols = columns.filter(
    (c) => c.key !== primary.key && (!actionsCol || c.key !== actionsCol.key)
  );
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 14,
        padding: '16px 2px',
        borderBottom: last ? 'none' : '1px solid ' + inkA(theme, 0.07),
      }}
    >
      <div style={{ minWidth: 0 }}>{cells[primary.key]}</div>
      {metaCols.length ? (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px 22px' }}>
          {metaCols.map((c) => (
            <div
              key={c.key}
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 4,
                minWidth: 0,
              }}
            >
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 800,
                  letterSpacing: '0.6px',
                  textTransform: 'uppercase',
                  color: t.label,
                }}
              >
                {c.label}
              </span>
              <span
                style={{
                  fontSize: 14,
                  color: inkA(theme, 0.85),
                  display: 'inline-flex',
                  alignItems: 'center',
                  minWidth: 0,
                }}
              >
                {cells[c.key]}
              </span>
            </div>
          ))}
        </div>
      ) : null}
      {actionsCol ? <div>{cells[actionsCol.key]}</div> : null}
    </div>
  );
}

// Mobile loading card — hero shaped by the primary column's skeleton hint.
function SkeletonCard({ columns, last }) {
  const theme = useTheme();
  const primary = columns.find((c) => c.primary) || columns[0];
  const isMedia = primary && primary.skeleton === 'media';
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 14,
        padding: '16px 2px',
        borderBottom: last ? 'none' : '1px solid ' + inkA(theme, 0.07),
      }}
    >
      {isMedia ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <SkeletonBar $w="56px" $h="40px" $r="8px" />
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 6,
              flex: 1,
            }}
          >
            <SkeletonBar $w="55%" />
            <SkeletonBar $w="38%" $h="11px" />
          </div>
        </div>
      ) : (
        <SkeletonBar $w="55%" $h="16px" />
      )}
      <div style={{ display: 'flex', gap: 22 }}>
        <SkeletonBar $w="64px" />
        <SkeletonBar $w="48px" />
        <SkeletonBar $w="56px" />
      </div>
      <SkeletonBar $w="150px" $h="30px" $r="9px" />
    </div>
  );
}

const DataTable = ({
  columns,
  data,
  renderRow,
  keyField = 'id',
  loading = false,
  emptyLabel = 'No records.',
  toolbar,
  filter,
  page,
  pageCount = 1,
  onPageChange,
  // Optional custom mobile renderer: (item, index, last) => node. Without it
  // the generic label/value MobileCard is used.
  mobileCard,
}) => {
  const t = tokens(useTheme());
  const isMobile = useIsMobile();
  const cols = columns.map((c) => c.width || 'minmax(0, 1fr)').join(' ');
  const keyOf = (item, i) => (item[keyField] != null ? item[keyField] : i);
  const skeleton = Array.from({ length: SKELETON_ROWS });
  const empty = (
    <div
      style={{
        padding: '34px 24px',
        textAlign: 'center',
        color: t.faint,
        fontSize: 14,
      }}
    >
      {emptyLabel}
    </div>
  );

  const renderMobileList = () => {
    if (loading) {
      return skeleton.map((_, i) => (
        <SkeletonCard
          key={i}
          columns={columns}
          last={i === SKELETON_ROWS - 1}
        />
      ));
    }
    if (!data.length) return empty;
    return data.map((item, i) =>
      mobileCard ? (
        <React.Fragment key={keyOf(item, i)}>
          {mobileCard(item, i, i === data.length - 1)}
        </React.Fragment>
      ) : (
        <MobileCard
          key={keyOf(item, i)}
          columns={columns}
          cells={renderRow(item)}
          last={i === data.length - 1}
        />
      )
    );
  };

  return (
    <div
      style={{
        background: t.card,
        border: '1px solid ' + t.border,
        borderRadius: 16,
        padding: isMobile ? '16px 14px 6px' : '22px 22px 8px',
        boxShadow: t.shadowCard,
      }}
    >
      {toolbar ? (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: isMobile ? 10 : 16,
            marginBottom: 16,
          }}
        >
          {toolbar}
        </div>
      ) : null}
      {filter ? <div style={{ marginBottom: 18 }}>{filter}</div> : null}

      {isMobile ? (
        <div
          style={{
            border: '1px solid ' + t.border,
            borderRadius: 12,
            marginBottom: 14,
            padding: '0 12px',
          }}
        >
          {renderMobileList()}
        </div>
      ) : (
        <div
          style={{
            border: '1px solid ' + t.border,
            borderRadius: 12,
            overflowX: 'auto',
            overflowY: 'hidden',
            marginBottom: 14,
          }}
        >
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: cols,
              columnGap: 18,
              alignItems: 'center',
              padding: '18px 24px',
              background: t.head,
              borderBottom: '1px solid ' + t.border,
            }}
          >
            {columns.map((c) => (
              <HeaderCell key={c.key} align={c.align}>
                {c.label}
              </HeaderCell>
            ))}
          </div>
          {loading
            ? skeleton.map((_, i) => (
                <SkeletonRow
                  key={i}
                  cols={cols}
                  columns={columns}
                  last={i === SKELETON_ROWS - 1}
                />
              ))
            : data.length
            ? data.map((item, i) => (
                <Row
                  key={keyOf(item, i)}
                  cols={cols}
                  columns={columns}
                  cells={renderRow(item)}
                  last={i === data.length - 1}
                />
              ))
            : empty}
        </div>
      )}

      {!loading && pageCount > 1 ? (
        <Pagination page={page} pageCount={pageCount} onChange={onPageChange} />
      ) : null}
    </div>
  );
};

export default DataTable;
